import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DynamicDataSheetGrid, textColumn, keyColumn, Column } from 'react-datasheet-grid';
import 'react-datasheet-grid/dist/style.css';
import Papa from 'papaparse';
import styled from 'styled-components';

interface IProps {
  value: string;
  onChange?: (csv: string) => void;
  readOnly?: boolean;
}

type Row = Record<string, string | null>;

const CHAR_WIDTH = 10;
const MIN_COL_WIDTH = 60;
const MAX_COL_WIDTH = 500;
const COL_PADDING = 24;
const RESIZE_HANDLE_WIDTH = 6;

function parseCsv(raw: string): { rows: Row[]; headers: string[]; delimiter: string } {
  if (!raw || !raw.trim()) return { rows: [], headers: [], delimiter: ',' };
  const result = Papa.parse<Row>(raw.trim(), { header: true, skipEmptyLines: true, delimiter: '' });
  const headers = result.meta.fields || [];
  const delimiter = result.meta.delimiter || ',';
  return { rows: result.data, headers, delimiter };
}

function calcColumnWidths(headers: string[], rows: Row[]): number[] {
  return headers.map((h) => {
    let maxLen = h.length;
    const limit = Math.min(rows.length, 100);
    for (let i = 0; i < limit; i++) {
      const val = rows[i][h];
      if (val && val.length > maxLen) maxLen = val.length;
    }
    return Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, maxLen * CHAR_WIDTH + COL_PADDING));
  });
}

const CsvEditor: React.FC<IProps> = ({ value, onChange, readOnly }) => {
  const { rows, headers, delimiter } = useMemo(() => parseCsv(value), [value]);
  const autoWidths = useMemo(() => calcColumnWidths(headers, rows), [headers, rows]);
  const [resizedWidths, setResizedWidths] = useState<number[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ colIndex: number; startX: number; startWidth: number } | null>(null);
  const mountedRef = useRef(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    // Delay mount so the grid doesn't race with parent layout
    const id = requestAnimationFrame(() => { if (mountedRef.current) setReady(true); });
    return () => { mountedRef.current = false; cancelAnimationFrame(id); };
  }, []);

  // Reset resized widths when headers change (different file)
  useEffect(() => {
    setResizedWidths([]);
  }, [headers.join(',')]);

  const colWidths = resizedWidths.length === headers.length ? resizedWidths : autoWidths;

  const columns: Column<Row>[] = useMemo(
    () =>
      headers.map((h, i) => ({
        ...keyColumn<Row, string>(h, textColumn),
        title: h,
        minWidth: colWidths[i] || MIN_COL_WIDTH,
        maxWidth: colWidths[i] || MIN_COL_WIDTH,
        disabled: readOnly || false,
      })),
    [headers, colWidths, readOnly],
  );

  // Attach resize handles to header cells
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.classList.contains('csv-resize-handle')) return;
      const colIndex = Number(target.dataset.col);
      if (isNaN(colIndex)) return;

      e.preventDefault();
      const currentWidths = resizedWidths.length === headers.length ? [...resizedWidths] : [...autoWidths];
      dragRef.current = { colIndex, startX: e.clientX, startWidth: currentWidths[colIndex] };

      const handleMouseMove = (me: MouseEvent) => {
        if (!dragRef.current) return;
        const diff = me.clientX - dragRef.current.startX;
        const newWidth = Math.max(MIN_COL_WIDTH, dragRef.current.startWidth + diff);
        currentWidths[dragRef.current.colIndex] = newWidth;
        setResizedWidths([...currentWidths]);
      };

      const handleMouseUp = () => {
        dragRef.current = null;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    container.addEventListener('mousedown', handleMouseDown);
    return () => container.removeEventListener('mousedown', handleMouseDown);
  }, [headers, autoWidths, resizedWidths]);

  // Inject resize handles into header cells
  useEffect(() => {
    const container = containerRef.current;
    if (!container || headers.length === 0) return;

    const injectHandles = () => {
      // .dsg-cell-header are the header cells; exclude .dsg-cell-gutter (row number column)
      const headerCells = container.querySelectorAll('.dsg-cell-header:not(.dsg-cell-gutter)');
      headerCells.forEach((cell, i) => {
        if (i >= headers.length) return;
        if (cell.querySelector('.csv-resize-handle')) return;

        const handle = document.createElement('div');
        handle.className = 'csv-resize-handle';
        handle.dataset.col = String(i);
        cell.appendChild(handle);
      });
    };

    // Run after the grid renders
    requestAnimationFrame(injectHandles);
  }, [headers, columns]);

  const createRow = useCallback(
    (): Row => headers.reduce<Row>((acc, h) => { acc[h] = ''; return acc; }, {}),
    [headers],
  );

  const handleChange = useCallback(
    (newRows: Row[]) => {
      if (!onChange) return;
      const csv = Papa.unparse(newRows, { columns: headers, delimiter });
      onChange(csv);
    },
    [headers, delimiter, onChange],
  );

  if (headers.length === 0) {
    return <Empty>No CSV data to display</Empty>;
  }

  if (!ready) {
    return <Container ref={containerRef} />;
  }

  return (
    <Container ref={containerRef}>
      <DynamicDataSheetGrid
        value={rows}
        onChange={readOnly ? undefined : handleChange}
        columns={columns}
        createRow={createRow}
        lockRows={readOnly || false}
        disableExpandSelection
      />
    </Container>
  );
};

export default CsvEditor;

const Container = styled.div`
  flex: 1;
  min-height: 0;
  padding: 8px;
  font-size: 13px;
  .dsg-container {
    height: 100% !important;
  }
  .csv-resize-handle {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: ${RESIZE_HANDLE_WIDTH}px;
    cursor: col-resize;
    z-index: 50;
    &:hover {
      background: rgba(25, 118, 210, 0.3);
    }
  }
`;

const Empty = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
`;
